import {
  MutableRefObject,
  RefObject,
  useEffect,
  useRef,
  useState,
} from "react";
import { Paper } from "@mui/material";
import { styled } from "@mui/material/styles";
import {
  DataGrid,
  GridCellParams,
  GridColDef,
  GridValidRowModel,
} from "@mui/x-data-grid";
import {
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { isCopyableGridField } from "../../utils/conditions";
import { formatValueType } from "../../utils/valueType";

const GridContainer = styled(Paper)(({ theme }) => ({
  flex: 1,
  width: "100%",
  overflow: "auto",
  display: "flex",
  flexDirection: "column",
  minHeight: 0,
  padding: theme.spacing(2),
  boxSizing: "border-box",
  position: "relative",
}));

type GridContentProps = {
  rows: GridValidRowModel[];
  columns: GridColDef[];
  gridApiRef: MutableRefObject<any>;
  gridContainerRef: RefObject<HTMLDivElement>;
  sortModel: any[];
  setSortModel: (next: any[]) => void;
  loading: boolean;
  activeId: string | number | null;
  overId: string | number | null;
  activeCell: { rowId: string | number; field: string } | null;
  recentlyEditedCells: Set<string>;
  setRecentlyEditedCells: (next: Set<string>) => void;
  pasteErrorCells: Set<string>;
  setPasteErrorCells: (next: Set<string>) => void;
  setActiveCell: (next: { rowId: string | number; field: string }) => void;
  isRowFieldEditable: (row: GridValidRowModel, field: string) => boolean;
  setEditingCell: (next: {
    rowId: string | number;
    field: string;
    initialValue?: string;
  }) => void;
  isCellEditable: (params: GridCellParams) => boolean;
  handlePaste: (event: React.ClipboardEvent<HTMLDivElement>) => void;
  onCopySuccess: (message: string) => void;
};

const GridContent = ({
  rows,
  columns,
  gridApiRef,
  gridContainerRef,
  sortModel,
  setSortModel,
  loading,
  activeId,
  overId,
  activeCell,
  recentlyEditedCells,
  setRecentlyEditedCells,
  pasteErrorCells,
  setPasteErrorCells,
  setActiveCell,
  isRowFieldEditable,
  setEditingCell,
  isCellEditable,
  handlePaste,
  onCopySuccess,
}: GridContentProps) => {
  const [rowSelectionModel, setRowSelectionModel] = useState<any[]>([]);
  const gridWrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (recentlyEditedCells.size === 0 && pasteErrorCells.size === 0) {
      return undefined;
    }

    const handlePointerDown = (event: PointerEvent) => {
      const wrapper = gridWrapperRef.current;
      if (!wrapper || wrapper.contains(event.target as Node)) {
        return;
      }

      setRecentlyEditedCells(new Set());
      setPasteErrorCells(new Set());
    };

    document.addEventListener("pointerdown", handlePointerDown, true);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown, true);
    };
  }, [
    recentlyEditedCells.size,
    pasteErrorCells.size,
    setRecentlyEditedCells,
    setPasteErrorCells,
  ]);

  const clearPasteHighlights = () => {
    if (recentlyEditedCells.size > 0) {
      setRecentlyEditedCells(new Set());
    }
    if (pasteErrorCells.size > 0) {
      setPasteErrorCells(new Set());
    }
  };

  const getCopyableFields = (): string[] => {
    return columns.map((col) => col.field).filter(isCopyableGridField);
  };

  const copySelectedRows = () => {
    const selectedRows = rows.filter((row) => rowSelectionModel.includes(row.id));
    const visibleFields = getCopyableFields();

    const headers = ["Condition ID", "Condition Name"]
      .concat(
        columns
          .filter((col) => visibleFields.includes(col.field))
          .map((col) => col.headerName || col.field)
      )
      .join("\t");

    const dataRows = selectedRows
      .map((row) => {
        const values = [
          formatValueType(row.conditionId),
          formatValueType(row.conditionName),
        ].concat(
          visibleFields.map((field) =>
            formatValueType(row[field] as ValueType | null)
          )
        );
        return values.join("\t");
      })
      .join("\n");

    const textToCopy = `${headers}\n${dataRows}`;

    navigator.clipboard.writeText(textToCopy);
    onCopySuccess(
      `Copied ${selectedRows.length} row${selectedRows.length === 1 ? "" : "s"}`
    );
  };

  return (
    <GridContainer elevation={1} ref={gridContainerRef}>
      <SortableContext
        items={rows.map((row) => row.id)}
        strategy={verticalListSortingStrategy}
      >
        <div
          ref={gridWrapperRef}
          onPasteCapture={handlePaste}
          onKeyDown={(event) => {
            if ((event.ctrlKey || event.metaKey) && event.key === "c") {
              if (rowSelectionModel.length > 0) {
                event.preventDefault();
                event.stopPropagation();
                copySelectedRows();
              }
            }
          }}
        >
          <DataGrid
            key={`datagrid-${rows.length}`}
            apiRef={gridApiRef}
            rows={rows}
            columns={columns}
            checkboxSelection
            disableRowSelectionOnClick={true}
            disableColumnMenu
            loading={loading}
            hideFooter={rows.length < 100}
            autoHeight={false}
            getRowHeight={() => "auto"}
            rowSelectionModel={rowSelectionModel}
            onRowSelectionModelChange={(newSelection) => {
              setRowSelectionModel(newSelection as any[]);
            }}
            sortModel={sortModel}
            onSortModelChange={(newSortModel) => {
              setSortModel(newSortModel as any[]);
            }}
            onCellClick={(params) => {
              clearPasteHighlights();
              setActiveCell({ rowId: params.id, field: params.field });
            }}
            onCellDoubleClick={(params, event) => {
              const canEdit = isRowFieldEditable(
                params.row as GridValidRowModel,
                params.field
              );
              if (!canEdit) {
                return;
              }

              event.preventDefault();
              event.stopPropagation();

              setEditingCell({
                rowId: params.id,
                field: params.field,
              });

              clearPasteHighlights();
            }}
            getCellClassName={(params) => {
              const classes = [];
              const cellKey = `${params.id}::${params.field}`;
              if (
                params.field !== "__check__" &&
                params.field !== "dragHandle"
              ) {
                classes.push("bordered-cell");
              }

              if (recentlyEditedCells.has(cellKey)) {
                classes.push("recently-edited-cell");
              }

              if (pasteErrorCells.has(cellKey)) {
                classes.push("paste-error-cell");
              }

              if (
                activeCell &&
                String(activeCell.rowId) === String(params.id) &&
                activeCell.field === params.field
              ) {
                classes.push("selected-cell");
              }

              const isPropertyField =
                params.field === "conditionName" ||
                isCopyableGridField(params.field);
              const isEditablePropertyField =
                isPropertyField &&
                isRowFieldEditable(
                  params.row as GridValidRowModel,
                  params.field
                );
              if (isEditablePropertyField) {
                classes.push("editable-cell");
              }

              if (isPropertyField && !isEditablePropertyField) {
                classes.push("non-editable-cell");
              }

              return classes.join(" ");
            }}
            getRowClassName={(params) => {
              const classes = [];
              if (params.id === activeId) classes.push("dragging");
              if (params.id === overId && activeId !== overId) {
                const activeIndex = rows.findIndex((r) => r.id === activeId);
                const overIndex = rows.findIndex((r) => r.id === overId);
                if (activeIndex < overIndex) {
                  classes.push("drop-below");
                } else {
                  classes.push("drop-above");
                }
              }
              return classes.join(" ");
            }}
            onCellKeyDown={(params, event) => {
              setActiveCell({ rowId: params.id, field: params.field });

              if ((event.ctrlKey || event.metaKey) && event.key === "c") {
                event.preventDefault();
                event.stopPropagation();

                if (rowSelectionModel.length > 0) {
                  copySelectedRows();
                } else {
                  const textToCopy = formatValueType(
                    params.value as ValueType | null
                  );
                  if (textToCopy) {
                    navigator.clipboard.writeText(textToCopy);
                    onCopySuccess("Copied cell");
                  }
                }
                return;
              }

              // Type-to-edit: when a focused cell is editable and the user
              // types a single printable character (no modifier keys), enter
              // edit mode and seed the editor with that character. Mirrors
              // the spreadsheet UX where the user does not need to
              // double-click first.
              const isPrintable =
                event.key.length === 1 &&
                !event.ctrlKey &&
                !event.metaKey &&
                !event.altKey;
              if (isPrintable) {
                const canEdit = isRowFieldEditable(
                  params.row as GridValidRowModel,
                  params.field
                );
                if (!canEdit) {
                  return;
                }
                event.preventDefault();
                event.stopPropagation();
                setEditingCell({
                  rowId: params.id,
                  field: params.field,
                  initialValue: event.key,
                });
                clearPasteHighlights();
              }
            }}
            isCellEditable={isCellEditable}
            sx={{
              height: "100%",
              width: "100%",
              "& .MuiDataGrid-columnHeaders": {
                borderBottom: "1px solid #e0e0e0",
                minHeight: "48px !important",
                maxHeight: "48px !important",
                lineHeight: "48px !important",
              },
              "& .MuiDataGrid-columnHeader": {
                backgroundColor: "#f5f5f5",
                height: "48px !important",
                whiteSpace: "nowrap",
              },
              "& .MuiDataGrid-columnHeaderTitleContainer": {
                padding: 0,
              },
              "& .MuiDataGrid-columnHeaderTitle": {
                fontSize: "11px",
                fontWeight: 600,
                color: "#666",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                whiteSpace: "nowrap",
                overflow: "visible",
              },
              "& .MuiDataGrid-cell": {
                fontSize: "13px",
                color: "#333",
                borderBottom: "1px solid #f0f0f0",
                padding: "8px 12px",
                lineHeight: 1.4,
                minHeight: "52px !important",
                whiteSpace: "normal",
                alignItems: "center",
                justifyContent: "flex-start",
                textAlign: "left",
              },
              "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": {
                outline: "none",
              },
              "& .MuiDataGrid-cellContent": {
                alignItems: "center",
                display: "flex",
                height: "100%",
                overflow: "visible",
                textAlign: "left",
                textOverflow: "clip",
                whiteSpace: "normal",
                overflowWrap: "anywhere",
                width: "100%",
              },
              "& .MuiDataGrid-cell.bordered-cell": {
                boxShadow: "inset 0 0 0 1px rgba(0, 0, 0, 0.08)",
              },
              "& .MuiDataGrid-cell.editable-cell": {
                backgroundColor: "#fff",
                boxShadow: "inset 0 0 0 1px rgba(25, 118, 210, 0.18)",
              },
              "& .MuiDataGrid-cell.editable-cell:hover": {
                backgroundColor: "rgba(25, 118, 210, 0.04)",
                boxShadow: "inset 0 0 0 1px rgba(25, 118, 210, 0.45)",
              },
              "& .MuiDataGrid-cell.selected-cell": {
                outline: "2px solid #1976d2",
                outlineOffset: "-2px",
                backgroundColor: "rgba(25, 118, 210, 0.08)",
              },
              "& .MuiDataGrid-cell.recently-edited-cell": {
                outline: "2px solid #fbc02d",
                outlineOffset: "-2px",
                backgroundColor: "rgba(251, 192, 45, 0.08)",
              },
              "& .MuiDataGrid-cell.paste-error-cell": {
                outline: "2px solid #d32f2f",
                outlineOffset: "-2px",
                backgroundColor: "rgba(211, 47, 47, 0.08)",
              },
              "& .MuiDataGrid-cell.non-editable-cell": {
                color: "rgba(0, 0, 0, 0.5)",
                backgroundColor: "#fafafa",
              },
              "& .MuiDataGrid-row.Mui-selected .MuiDataGrid-cell": {
                backgroundColor: "#e0e0e0 !important",
              },
              "& .MuiDataGrid-row.Mui-selected:hover .MuiDataGrid-cell": {
                backgroundColor: "#d0d0d0 !important",
              },
              "& .MuiDataGrid-virtualScroller": {
                marginTop: "0 !important",
                overflow: "auto !important",
              },
              "& .MuiCheckbox-root": {
                color: "#1E5471",
              },
              "& .MuiCheckbox-root.Mui-checked": {
                color: "#1E5471",
              },
              "& .MuiDataGrid-row.Mui-selected": {
                backgroundColor: "#e0e0e0 !important",
              },
              "& .MuiDataGrid-row.Mui-selected:hover": {
                backgroundColor: "#d0d0d0 !important",
              },
              "& .MuiDataGrid-row": {
                position: "relative",
                "&.dragging": {
                  opacity: 0.5,
                },
                "&.drop-above::before": {
                  content: '""',
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  height: "2px",
                  backgroundColor: "#1E5471",
                  zIndex: 10,
                },
                "&.drop-below::after": {
                  content: '""',
                  position: "absolute",
                  bottom: 0,
                  left: 0,
                  right: 0,
                  height: "2px",
                  backgroundColor: "#1E5471",
                  zIndex: 10,
                },
              },
            }}
          />
        </div>
      </SortableContext>
    </GridContainer>
  );
};

export default GridContent;
